'use client';

import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  toast,
} from '@ais-ui/ui-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocalization } from '@ais-ui/locale-management';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import React, { useEffect, useMemo, useState } from 'react';
import { ShapeSelect } from '../../Shapes/ShapeSelects/ShapeSelect';
import { cn } from '@ais-ui/utils';
import { useSaveSettings } from '@ais-ui/websockets';
import { useSettingsPage } from '../SettingsPageProvider';

const notificationsFormSchema = z.object({
  emergency_alerts_mdm: z.boolean(),
  emergency_area: z.string(),
});

type NotificationsFormValues = z.infer<typeof notificationsFormSchema>;

const defaultValues: Partial<NotificationsFormValues> = {
  emergency_alerts_mdm: false,
  emergency_area: '',
};

export function NotificationsForm() {
  const { settings } = useSettings();
  const { messages } = useLocalization();
  const [formInitialized, setFormInitialized] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const form = useForm<NotificationsFormValues>({
    resolver: zodResolver(notificationsFormSchema),
    defaultValues,
  });

  const initialValues = {
    ...form.getValues(),
    emergency_alerts_mdm:
      !!settings['ribbonPlugin.settings']?.emergency_alerts_mdm,
    emergency_area: settings['ribbonPlugin.settings']?.emergency_area ?? '',
  };

  const emergencyAlerts = useMemo(
    () => form.watch('emergency_alerts_mdm'),
    [form.watch('emergency_alerts_mdm')]
  );

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  useEffect(() => {
    if (!formInitialized) {
      form.reset(initialValues);
      setFormInitialized(true); // Prevent re-initialization
    }
  }, [form, formInitialized, settings]);

  function onSubmit(data: NotificationsFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        emergency_alerts: data.emergency_alerts_mdm, // backwards compatibility
        emergency_alerts_mdm: data.emergency_alerts_mdm,
        emergency_area: data.emergency_area === '' ? null : data.emergency_area,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-notifications-enable-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsNotificationSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-messages-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsNotificationFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          key="emergency_alerts_mdm"
          control={form.control}
          name="emergency_alerts_mdm"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Notify me about...</FormLabel>
              <FormItem className="flex items-center space-x-3 space-y-0">
                <FormControl>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="emergency-alerts"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <label
                      htmlFor="terms"
                      className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Emergency alerts
                    </label>
                  </div>
                </FormControl>
              </FormItem>
              <FormMessage />
            </FormItem>
          )}
        />
        {emergencyAlerts && (
          <FormField
            key="emergency_area"
            control={form.control}
            name="emergency_area"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-between border rounded-lg p-4'
                )}
              >
                <div className="space-y-0.5">
                  <div className="flex justify-between">
                    <FormLabel className="text-base">
                      {messages.settingsNotificationsEmergencyShapeLabel}
                    </FormLabel>
                    <InfoIconTooltip>
                      {messages.settingsNotificationsEmergencyShapeTooltip}
                    </InfoIconTooltip>
                  </div>

                  <FormDescription>
                    {messages.settingsNotificationsEmergencyShapeDescription}
                  </FormDescription>
                </div>
                <FormControl
                  id="settings-emergency-alerts-shape"
                  onChange={() => form.clearErrors()}
                >
                  <ShapeSelect
                    className="w-72"
                    id="emergency-alerts-shape-select"
                    value={field.value}
                    onChange={field.onChange}
                    allowedGeometryTypes={['Polygon']}
                  ></ShapeSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-notification-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
}
